import type React from 'react'
import { createContext, useContext, useState } from 'react'
import { useTags } from '../hooks/useTags'
import { type TagFilters, tagService } from '../services/tagService'
import type { CreateTagData, Tag, TagStatus } from '../types/tag'

interface TagContextType {
  tags: Tag[]
  isLoading: boolean
  error: string | null
  isOffline: boolean
  filters: TagFilters | undefined
  setFilters: (filters: TagFilters | undefined) => void
  createTag: (tagData: CreateTagData) => Promise<Tag>
  updateTagStatus: (tagId: string, status: TagStatus) => Promise<void>
  completeTagWithSignature: (
    tagId: string,
    signatureData: string,
  ) => Promise<void>
  refreshTags: (force?: boolean, currentFilters?: TagFilters) => Promise<void>
  refreshCreatedTags: () => Promise<void>
  getCurrentTag: (tagId?: string) => Tag | undefined
  submitDriverSignature: (tagId: string, signatureData: string) => Promise<void>
  patchTag: (tagId: string, updates: Partial<Tag>) => Promise<Tag>
  refreshTag: (tagId: string) => Promise<void>
}

const TagContext = createContext<TagContextType | undefined>(undefined)

export const TagProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [filters, setFilters] = useState<TagFilters | undefined>(undefined)
  const tagsData = useTags(filters)

  const value = {
    ...tagsData,
    filters,
    setFilters,
    patchTag: tagService.patchTag,
  }

  return <TagContext.Provider value={value}>{children}</TagContext.Provider>
}

export const useTagContext = () => {
  const context = useContext(TagContext)
  if (!context) {
    throw new Error('useTagContext must be used within a TagProvider')
  }
  return context
}
